import { maxBy } from 'lodash';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useFullscreen } from '../../../layouts/StackedLayout/StackedLayout';
import StaticSummaryGroup, { type StaticSummaryGroupItem } from './StaticSummaryGroup';
import { createRadialLabelTickRenderer } from './utils';

const wordWrapBreakPoint = 400;

export interface CategoryRadarSectionDatum {
    shortLabel: string;
    value: number;
}

export interface CategoryRadarSectionSummaryCard {
    label: string;
    value: number;
    total: number;
}

export interface CategoryRadarSectionStatistics {
    domainMax: number;
    radarData: CategoryRadarSectionDatum[];
    summaryCards: CategoryRadarSectionSummaryCard[];
    tickCount?: number;
}

export interface CategoryRadarSectionChartMargin {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
}

export interface CategoryRadarSectionProps {
    title?: ReactNode;
    statistics: CategoryRadarSectionStatistics;
    summaryFormation?: number[];
    className?: string;
    summaryValueDecimals?: number;
    tooltipValueLabel?: string;
    tooltipAxisLabel?: string;
    chartMargin?: CategoryRadarSectionChartMargin;
}

const CategoryRadarSection = ({
    title,
    statistics,
    summaryFormation = [3],
    className,
    summaryValueDecimals = 1,
    tooltipValueLabel,
    tooltipAxisLabel,
    chartMargin,
}: CategoryRadarSectionProps) => {
    const { t } = useTranslation('common');
    const { isMobile } = useFullscreen();
    const containerRef = useRef<HTMLDivElement>(null);
    const [wrapWords, setWrapWords] = useState(false);
    const axisCount = statistics.radarData.length;
    const isSmallAxisCount = axisCount <= 3;

    const summaryItems: StaticSummaryGroupItem[] = useMemo(
        () =>
            statistics.summaryCards.map(item => ({
                title: `${item.value.toFixed(summaryValueDecimals)}/${item.total}`,
                description: item.label,
            })),
        [statistics.summaryCards, summaryValueDecimals]
    );

    const domain = useMemo(
        () => Math.max(0, maxBy(statistics.summaryCards, 'total')?.total || 0, statistics.domainMax),
        [statistics.domainMax, statistics.summaryCards]
    );

    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        const resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                const width = entry.contentRect.width;
                setWrapWords(width < wordWrapBreakPoint);
            }
        });

        resizeObserver.observe(containerRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    const tickCount = statistics.tickCount ?? (domain <= 10 ? domain + 1 : 8);

    const margin = useMemo(
        () => ({
            top: chartMargin?.top ?? 8,
            right: chartMargin?.right ?? (isMobile ? 44 : 64),
            bottom: chartMargin?.bottom ?? (isSmallAxisCount ? 12 : isMobile ? 24 : 32),
            left: chartMargin?.left ?? (isMobile ? 44 : 64),
        }),
        [chartMargin?.top, chartMargin?.right, chartMargin?.bottom, chartMargin?.left, isMobile, isSmallAxisCount]
    );

    return (
        <div className={className}>
            <div className="mx-auto grid max-w-3xl gap-5">
                <div className="w-full">
                    <StaticSummaryGroup formation={summaryFormation} isMobile={isMobile} items={summaryItems} />
                </div>

                <div
                    ref={containerRef}
                    className="flex w-full flex-col items-center justify-center gap-2.5 rounded-[10px] border border-neutral-300 py-5"
                >
                    <div
                        className={`w-full ${
                            isMobile ? 'aspect-3/2 max-h-[38vh]' : 'aspect-[495/281] max-h-[50vh] max-w-[495px]'
                        }`}
                    >
                        <ResponsiveContainer>
                            <RadarChart
                                outerRadius={isSmallAxisCount ? '82%' : '75%'}
                                cx="50%"
                                cy={isSmallAxisCount ? '55%' : '50%'}
                                data={statistics.radarData}
                                margin={margin}
                            >
                                <PolarGrid strokeDasharray="3 3" stroke="#b7b8b9" />
                                <PolarAngleAxis
                                    dataKey="shortLabel"
                                    tick={createRadialLabelTickRenderer(isMobile ? 12 : 16, {
                                        fontSize: 12,
                                        fill: 'var(--color-neutral-500)',
                                        fontWeight: 500,
                                        wrapWords,
                                    })}
                                    stroke="#2221212c"
                                />
                                <PolarRadiusAxis
                                    angle={90}
                                    domain={[0, domain]}
                                    tick={{
                                        fontSize: 11,
                                        fill: 'var(--color-neutral-500)',
                                        dy: 0,
                                    }}
                                    axisLine={{ stroke: '#dce1ebff' }}
                                    tickLine={{ stroke: '#f13bbbff' }}
                                    stroke="var(--color-slate-700)"
                                    tickCount={tickCount}
                                    tickFormatter={value => value.toString()}
                                    orientation="middle"
                                />
                                <Radar
                                    name={tooltipValueLabel ?? t('assessmentResults.yourScore')}
                                    dataKey="value"
                                    stroke="var(--color-teal-600)"
                                    fill="#1fac7d56"
                                    fillOpacity={0.2}
                                    strokeWidth={2.5}
                                    dot={true}
                                    activeDot={{ r: 5 }}
                                />
                                <Tooltip
                                    formatter={(value: string) => [
                                        value,
                                        tooltipValueLabel ?? t('assessmentResults.yourScore'),
                                    ]}
                                    labelFormatter={label =>
                                        `${tooltipAxisLabel ?? t('assessmentResults.subcategory')}: ${label}`
                                    }
                                    contentStyle={{
                                        borderRadius: '8px',
                                        backgroundColor: 'white',
                                        borderColor: '#c7c5c5ff',
                                        boxShadow: 'var(--shadow)',
                                        color: 'hsl(var(--foreground))',
                                        padding: '0.75rem 1rem',
                                    }}
                                />
                            </RadarChart>
                        </ResponsiveContainer>
                    </div>
                    {title ? (
                        <div className="w-full text-center text-base leading-6 font-semibold text-neutral-900">
                            {title}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default CategoryRadarSection;
